import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import HeaderApp from "../../components/HeaderApp";
import StudentCoursePdfViewer from "../../components/StudentCoursePdfViewer";
import StudentCourseQuiz from "../../components/StudentCourseQuiz";
import {
  getCurso,
  getSeccionesCurso,
  registrarFinalizacionCurso,
  registrarInicioCurso,
} from "../../services/cursosService";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { logAxiosError } from "../../utils/logAxiosError";

const resolveRutaArchivo = (seccion) =>
  seccion?.RutaArchivo ??
  seccion?.rutaArchivo ??
  seccion?.ArchivoUrl ??
  seccion?.archivoUrl ??
  seccion?.UrlArchivo ??
  seccion?.urlArchivo ??
  seccion?.Archivo ??
  seccion?.archivo ??
  "";

const normalizarSecciones = (response) => {
  const data = response?.data ?? response;
  const list = Array.isArray(data) ? data : [];
  if (!Array.isArray(list)) return [];

  return list
    .map((s, index) => ({
      id:
        s?.idSeccionCurso ??
        s?.IdSeccionCurso ??
        s?.idSeccion ??
        s?.IdSeccion ??
        s?.id ??
        `idx-${index}`,
      nombre: s?.Nombre ?? s?.nombre ?? `Sección ${index + 1}`,
      orden: Number(s?.Orden ?? s?.orden ?? index + 1) || index + 1,
      estado: String(s?.Estado ?? s?.estado ?? "1"),
      archivoRuta: resolveRutaArchivo(s),
    }))
    .filter((s) => s.estado === "1" || s.estado === "true" || Number(s.estado) === 1)
    .sort((a, b) => a.orden - b.orden);
};

const StudentCourseStudy = () => {
  const { idCurso } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  const [tituloCurso, setTituloCurso] = useState("");
  const [cantidadPreguntasCurso, setCantidadPreguntasCurso] = useState(10);
  const [porcentajeAprobacionCurso, setPorcentajeAprobacionCurso] = useState(70);
  const [secciones, setSecciones] = useState([]);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scrollEndForStep, setScrollEndForStep] = useState(false);
  const [mostrarCuestionario, setMostrarCuestionario] = useState(false);
  const inicioRegistradoRef = useRef(false);

  // Rol 1 = estudiante; rol 2 = administrador (coherente con Login, Sidebar y página Course)
  const isStudent = String(user?.idRol) !== "2";

  useEffect(() => {
    setScrollEndForStep(false);
  }, [step]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!idCurso) return;
      setLoading(true);
      setError("");
      try {
        const [cursoRes, secRes] = await Promise.all([getCurso(idCurso), getSeccionesCurso(idCurso)]);
        if (cancelled) return;
        const cursoData = cursoRes?.data ?? cursoRes;
        const nombre =
          cursoData?.Nombre ?? cursoData?.nombre ?? cursoRes?.Nombre ?? cursoRes?.nombre ?? "Curso";
        const cantidadPreguntas = Number(
          cursoData?.CantidadPreguntas ??
            cursoData?.cantidadPreguntas ??
            cursoRes?.CantidadPreguntas ??
            cursoRes?.cantidadPreguntas ??
            10
        );
        const porcentajeAprobacion = Number(
          cursoData?.PorcentajeAprobacion ??
            cursoData?.porcentajeAprobacion ??
            cursoRes?.PorcentajeAprobacion ??
            cursoRes?.porcentajeAprobacion ??
            70
        );
        setTituloCurso(nombre);
        setCantidadPreguntasCurso(
          Number.isFinite(cantidadPreguntas) && cantidadPreguntas >= 1 ? cantidadPreguntas : 10
        );
        setPorcentajeAprobacionCurso(
          Number.isFinite(porcentajeAprobacion) &&
            porcentajeAprobacion >= 1 &&
            porcentajeAprobacion <= 100
            ? porcentajeAprobacion
            : 70
        );
        const list = normalizarSecciones(secRes);
        setSecciones(list);
        setStep(0);
        setMostrarCuestionario(false);
      } catch (e) {
        logAxiosError("Estudiante: getCurso / getSeccionesCurso", e);
        if (!cancelled) setError("No se pudo cargar el curso.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [idCurso]);

  useEffect(() => {
    if (!idCurso || !isStudent || inicioRegistradoRef.current) return;
    let cancel = false;
    const run = async () => {
      try {
        await registrarInicioCurso(idCurso);
        if (!cancel) inicioRegistradoRef.current = true;
      } catch (e) {
        logAxiosError(`Estudiante: registrar inicio curso ${idCurso}`, e);
      }
    };
    run();
    return () => {
      cancel = true;
    };
  }, [idCurso, isStudent]);

  const seccionActual = secciones[step];
  const esUltimoPaso = step >= secciones.length - 1;
  const pdfWidth = isNarrow ? Math.min(window.innerWidth - 32, 720) : 720;

  const handleReachedScrollEnd = useCallback(() => {
    setScrollEndForStep(true);
  }, []);

  if (!isStudent) {
    return <Navigate to="/course" replace />;
  }

  if (!idCurso) {
    return <Navigate to="/available-courses" replace />;
  }

  return (
    <>
      <HeaderApp />
      <Box component="main" sx={{ py: 3, px: 2, bgcolor: "background.default", minHeight: "calc(100vh - 64px)" }}>
        <Container maxWidth="md">
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/available-courses")} sx={{ mb: 2 }}>
            Volver a cursos
          </Button>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            {tituloCurso}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Recorre cada sección. Podrás continuar cuando llegues al final del PDF.
          </Typography>

          {loading && (
            <Box py={8} display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}

          {!loading && !error && secciones.length === 0 && (
            <Typography color="text.secondary">Este curso no tiene secciones publicadas.</Typography>
          )}

          {!loading && !error && mostrarCuestionario && (
            <StudentCourseQuiz
              idCurso={idCurso}
              tituloCurso={tituloCurso}
              cantidadPreguntas={cantidadPreguntasCurso}
              porcentajeAprobacion={porcentajeAprobacionCurso}
              user={user}
              onFinalizarCurso={async ({ ultimaCalificacion, aprobo, variablesSeguimiento }) => {
                await registrarFinalizacionCurso(idCurso, {
                  UltimaCalificacion: ultimaCalificacion,
                  Aprobo: aprobo ? 1 : 0,
                  VariablesSeguimiento: variablesSeguimiento ?? null,
                });

                if (!aprobo) {
                  return { redirectToCertificate: false };
                }

                navigate(`/curso/${idCurso}/certificado`, {
                  state: {
                    aprobo,
                    nombreCurso: tituloCurso,
                    fechaFinalizacion: new Date().toISOString(),
                    nombreCompleto: [
                      user?.PrimerNombre ?? user?.nombre ?? user?.Nombre ?? "",
                      user?.PrimerApellido ?? user?.apellido ?? user?.Apellido ?? "",
                    ]
                      .join(" ")
                      .trim(),
                  },
                });
                return { redirectToCertificate: true };
              }}
              onSalir={() => navigate("/available-courses")}
            />
          )}

          {!loading && !error && !mostrarCuestionario && secciones.length > 0 && seccionActual && (
            <>
              <Stepper activeStep={step} alternativeLabel sx={{ mb: 3 }}>
                {secciones.map((s) => (
                  <Step key={String(s.id)}>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontSize: "0.75rem",
                          maxWidth: 100,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    >
                      {s.nombre}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                {seccionActual.nombre}
              </Typography>

              {!seccionActual.archivoRuta ? (
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Esta sección no tiene PDF asignado.
                </Typography>
              ) : (
                <StudentCoursePdfViewer
                  key={`${idCurso}-${seccionActual.id}-${seccionActual.archivoRuta}`}
                  idCurso={idCurso}
                  archivoRuta={seccionActual.archivoRuta}
                  onReachedEnd={handleReachedScrollEnd}
                  width={pdfWidth}
                />
              )}

              <Box display="flex" justifyContent="space-between" alignItems="center" mt={3} flexWrap="wrap" gap={2}>
                <Button
                  variant="outlined"
                  disabled={step === 0}
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                >
                  Anterior
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {!seccionActual.archivoRuta
                    ? "Sin PDF en esta sección"
                    : scrollEndForStep
                      ? "Listo para continuar"
                      : "Desplázate hasta el final del documento para continuar"}
                </Typography>
                <Button
                  variant="contained"
                  endIcon={!esUltimoPaso ? <ArrowForwardIcon /> : null}
                  disabled={
                    Boolean(seccionActual.archivoRuta) && !scrollEndForStep
                  }
                  onClick={() => {
                    if (!esUltimoPaso) setStep((s) => s + 1);
                    else setMostrarCuestionario(true);
                  }}
                >
                  {esUltimoPaso ? "Finalizar curso" : "Continuar"}
                </Button>
              </Box>
            </>
          )}
        </Container>
      </Box>
    </>
  );
};

export default StudentCourseStudy;
